import { CheckCircle, Download, FileSpreadsheet, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

const UnitBulkImport = ({ buildingId, buildingName, onSuccess }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // 템플릿 다운로드
  const downloadTemplate = async () => {
    try {
      const response = await api.get('/units/template', {
        responseType: 'blob'
      });
      
      // Excel 파일 다운로드
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `세대정보_템플릿_${buildingName}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('템플릿 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('템플릿 다운로드 오류:', error);
      toast.error('템플릿 다운로드에 실패했습니다.');
    }
  };

  // 파일 업로드
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 파일 형식 검증
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Excel 파일(.xlsx, .xls) 또는 CSV 파일만 업로드 가능합니다.');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);
    setShowResult(false);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('building_id', buildingId);

      console.log('🔍 업로드할 파일:', file);
      console.log('🔍 건물 ID:', buildingId);
      console.log('🔍 FormData 내용:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      const response = await api.post('/units/upload', formData);

      console.log('🔍 업로드 응답:', response);
      console.log('🔍 응답 타입:', typeof response);
      console.log('🔍 response.data:', response.data);
      
      // api.js 인터셉터에서 FormData 업로드는 원본 response를 반환하므로 response.data 확인
      const responseData = response.data || response;
      
      console.log('🔍 responseData:', responseData);
      console.log('🔍 created_count:', responseData.created_count);
      console.log('🔍 total_processed:', responseData.total_processed);
      console.log('🔍 failed_count:', responseData.failed_count);

      setUploadResult(responseData);
      setShowResult(true);
      
      // 성공한 세대가 있으면 onSuccess 호출
      if (responseData.created_count > 0) {
        toast.success(`✅ ${responseData.created_count}개 세대가 성공적으로 등록되었습니다!`);
        
        // 목록 갱신을 위해 onSuccess 호출 (성공한 세대가 있으면)
        if (onSuccess) {
          console.log('🔍 onSuccess 호출');
          // 약간의 지연을 두고 호출하여 상태 업데이트가 완료되도록 함
          setTimeout(() => {
            onSuccess();
          }, 100);
        }
      }
      
      if (responseData.failed_count > 0) {
        toast.error(`❌ ${responseData.failed_count}개 세대 등록에 실패했습니다.`);
      }
      
      if (responseData.warnings && responseData.warnings.length > 0) {
        toast.warning(`⚠️ ${responseData.warnings.length}개 요금플랜 경고가 있습니다.`, {
          duration: 5000
        });
      }
      
      // 파일 입력 초기화
      event.target.value = '';

    } catch (error) {
      console.error('파일 업로드 오류:', error);
      console.error('에러 응답:', error.response?.data);
      
      // 상세 에러 메시지 표시
      const errorMessage = error.response?.data?.error || error.response?.data?.message || '파일 업로드에 실패했습니다.';
      const errorDetails = error.response?.data?.details;
      
      if (errorDetails && Array.isArray(errorDetails) && errorDetails.length > 0) {
        // 상세 오류 목록 표시
        const detailsText = errorDetails.slice(0, 5).join('\n'); // 최대 5개만 표시
        toast.error(`${errorMessage}\n\n${detailsText}${errorDetails.length > 5 ? `\n... 외 ${errorDetails.length - 5}개 오류` : ''}`, {
          duration: 8000
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">세대 일괄 등록</h3>
        <div className="flex space-x-2">
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="mr-2 h-4 w-4" />
            템플릿 다운로드
          </button>
        </div>
      </div>

       <div className="mb-4">
         <p className="text-sm text-gray-600 mb-2">
           Excel 파일을 업로드하여 세대 정보를 일괄 등록할 수 있습니다.
         </p>
         <div className="text-xs text-gray-500">
           <p>• 필수 필드: 호수, 층수, 전용면적(m²), 세대타입, 상태, 기준월세(원), 기준보증금(원), 기준관리비(원)</p>
           <p>• 선택 필드: 요금플랜명 (임대관리수수료 요금플랜 이름, 예: Basic, Regular, Premium)</p>
           <p>• 세대타입: 원룸, 1.5룸, 투룸, 쓰리룸 이상</p>
           <p>• 상태: available(임대가능), occupied(임대중), maintenance(수리중)</p>
           <p>• 한글 인코딩: 템플릿 다운로드 시 UTF-8 BOM이 포함되어 한글이 깨지지 않습니다</p>
           <p>• Excel에서 저장 시 "CSV UTF-8(쉼표로 구분)" 형식으로 저장하세요</p>
         </div>
       </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <div className="text-center">
          <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <label htmlFor="file-upload" className="cursor-pointer">
              <span className="mt-2 block text-sm font-medium text-gray-900">
                {isUploading ? '업로드 중...' : 'Excel 파일 선택'}
              </span>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                disabled={isUploading}
              />
            </label>
            <p className="mt-1 text-xs text-gray-500">
              Excel 또는 CSV 파일을 드래그하거나 클릭하여 선택하세요
            </p>
          </div>
        </div>
      </div>

      {/* 업로드 결과 */}
      {showResult && uploadResult && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            📊 업로드 결과
          </h4>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{uploadResult.total_processed}</div>
              <div className="text-xs text-gray-500">총 처리</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{uploadResult.created_count}</div>
              <div className="text-xs text-gray-500">성공</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{uploadResult.failed_count}</div>
              <div className="text-xs text-gray-500">실패</div>
            </div>
          </div>

          {/* 성공한 세대 목록 */}
          {uploadResult.created_units && uploadResult.created_units.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-green-700 mb-2 flex items-center">
                <CheckCircle className="mr-1 h-4 w-4" />
                ✅ 등록된 세대 ({uploadResult.created_units.length}개)
              </h5>
              <div className="text-xs text-gray-600 max-h-32 overflow-y-auto bg-white p-2 rounded border">
                {uploadResult.created_units.map((unit, index) => (
                  <div key={index} className="flex justify-between py-1 border-b border-gray-100 last:border-b-0">
                    <span className="font-medium">{unit.unit_number}호</span>
                    <span>{unit.unit_type} - {unit.area}m²</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 경고 메시지 */}
          {uploadResult.warnings && uploadResult.warnings.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-yellow-700 mb-2 flex items-center">
                ⚠️ 경고 ({uploadResult.warnings.length}개)
              </h5>
              <div className="text-xs text-gray-600 max-h-32 overflow-y-auto bg-yellow-50 p-2 rounded border border-yellow-200">
                {uploadResult.warnings.map((warning, index) => (
                  <div key={index} className="py-1 border-b border-yellow-100 last:border-b-0">
                    <span className="text-yellow-800">{warning}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 실패한 세대 목록 */}
          {uploadResult.failed_units && uploadResult.failed_units.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-red-700 mb-2 flex items-center">
                <XCircle className="mr-1 h-4 w-4" />
                실패한 세대 ({uploadResult.failed_units.length}개)
              </h5>
              <div className="text-xs text-gray-600 max-h-32 overflow-y-auto">
                {uploadResult.failed_units.map((unit, index) => (
                  <div key={index} className="flex justify-between">
                    <span>{unit.unit_number}</span>
                    <span className="text-red-600">{unit.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnitBulkImport;
